import React, { FC } from 'react';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import MuiTable from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { IEmployeeBalancesTransDTO } from 'src/api/dtos';
import { TableBody, Tooltip } from 'src/shared/components';
import { ArrowDownIcon, ArrowUpIcon, ClearIcon, WarningFilledIcon } from 'src/shared/components/icons';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'src/shared/constants';
import { EmployeeBenefitStatusEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { formatDuration, formatTime, isAfterDay, isDateWithinDateRange, isSameDay } from 'src/shared/utils';
import { getDurationFormat, getTimeFormat } from 'src/store/user/user.selectors';

interface Props {
  startDate: string;
  endDate: string;
  startTime: string | null | undefined;
  endTime: string | null | undefined;
  transactions: IEmployeeBalancesTransDTO[];
  status: EmployeeBenefitStatusEnums;
}

export const AccrualBalanceBreakdownTable: FC<Props> = ({
  startDate,
  endDate,
  startTime,
  endTime,
  transactions,
  status,
}) => {
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);
  const durationFormat = useSelector(getDurationFormat);

  return (
    <Table>
      <THead>
        <TableRow>
          <TH scope="col">{t('global.date')}</TH>
          <TH scope="col">{t('global.amount')}</TH>
          <TH scope="col">{t('global.balance')}</TH>
          <TH scope="col">{t('global.action')}</TH>
        </TableRow>
      </THead>
      <TBody isEmpty={!transactions.length}>
        {transactions.map((item, index) => {
          const isSameStartDate = isSameDay(item.date, startDate);
          const isSameEndDate = isSameDay(item.date, endDate);
          const isBetweenSelectedDates = isDateWithinDateRange(item.date, { startDate, endDate });
          const isAfterEndDate = isAfterDay(item.date, endDate);
          const showTime = startTime && endTime && isSameStartDate;

          const highlightClass = isSameEndDate ? 'highlight' : '';
          const borderedTopClass = isSameStartDate ? 'highlight-border-top' : '';
          const borderedBottomClass = isSameEndDate ? 'highlight-border-bottom' : '';
          const borderedLeftClass = isBetweenSelectedDates ? 'highlight-border-left' : '';
          const borderedRightClass = isBetweenSelectedDates ? 'highlight-border-right' : '';
          const lowerOpacityClass = isAfterEndDate ? 'opacity-75' : '';
          const textDangerClass = item.hasError ? 'negative-balance' : '';
          const textMutedClass = isAfterEndDate ? 'text-muted' : '';

          const borderClasses = [borderedTopClass, borderedBottomClass].filter((c) => c.length > 0);
          const rowClasses = [lowerOpacityClass, textMutedClass].filter((c) => c.length > 0);

          const ErrorIcon = status === EmployeeBenefitStatusEnums.VIOLATED ? ClearIcon : WarningFilledIcon;
          const errorIconColor = status === EmployeeBenefitStatusEnums.VIOLATED ? 'error' : 'warning';

          const BalanceIcon = item.amountInSeconds < 0 ? ArrowDownIcon : ArrowUpIcon;

          return (
            <TableRow key={`breakdown-item-${index}`} className={rowClasses.join(' ')}>
              <TD sx={{ fontWeight: 500 }} className={`${borderClasses.join(' ')} ${borderedLeftClass}`}>
                {moment(item.date).format(DATE_FORMAT)}
                {showTime && (
                  <Typography sx={{ fontSize: 'inherit' }} color="text.secondary">
                    ({formatTime(startTime, timeFormat, DATE_TIME_FORMAT)} -{' '}
                    {formatTime(endTime, timeFormat, DATE_TIME_FORMAT)})
                  </Typography>
                )}
              </TD>
              <TD className={borderClasses.join(' ')}>
                <Stack direction="row" color="text.secondary" alignItems="center" spacing={1}>
                  <BalanceIcon size={20} />
                  <span>{formatDuration(Math.abs(item.amountInSeconds), durationFormat, 'seconds')}</span>
                </Stack>
              </TD>
              <TD className={`${borderClasses.join(' ')} ${textDangerClass} ${highlightClass}`}>
                <Stack direction="row" className={textDangerClass} alignItems="center" spacing={1}>
                  {formatDuration(item.balanceInSeconds, durationFormat, 'seconds')}
                  {!!item.errorMessage && (
                    <Tooltip title={item.errorMessage} variant="error">
                      <IconButton size="small" color={errorIconColor}>
                        <ErrorIcon size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TD>
              <TD sx={{ fontWeight: 500 }} className={`${borderClasses.join(' ')} ${borderedRightClass}`}>
                {item.actionName}
              </TD>
            </TableRow>
          );
        })}
      </TBody>
    </Table>
  );
};

const Table = styled(MuiTable)(({ theme }) => ({
  width: '100%',
  marginBottom: 0,
  borderTop: 0,
  borderCollapse: 'separate',
  borderSpacing: 0,
  borderRadius: '8px',
  fontSize: '0.75rem',

  td: {
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #dee2e6',
    color: theme.palette.text.secondary,

    '&:first-of-type': {
      borderLeft: '1px solid #dee2e6',
    },

    '&:last-of-type': {
      borderRight: '1px solid #dee2e6',
    },
  },

  '.highlight': {
    color: '#598F17',
    background: '#F3F8E7',

    '&.negative-balance': {
      color: '#C7322F',
      background: '#FDEBED',
    },
  },

  '.highlight-border-top': {
    borderTop: '1px solid #192128',
  },

  '.highlight-border-bottom': {
    borderBottom: '1px solid #192128',
  },

  '.highlight-border-left': {
    borderLeft: '1px solid #192128 !important',
  },

  '.highlight-border-right': {
    borderRight: '1px solid #192128 !important',
  },
}));

const THead = styled(TableHead)(() => ({
  width: '100%',
  display: 'table',
  tableLayout: 'fixed',
}));

const TBody = styled(TableBody)(({ theme }) => ({
  display: 'block',
  maxHeight: '320px',
  overflowY: 'auto',

  tr: {
    width: '100%',
    display: 'table',
    tableLayout: 'fixed',

    '.text-muted': {
      color: theme.palette.text.secondary,
    },
  },
}));

const TH = styled(TableCell)(() => ({
  verticalAlign: 'middle',
  padding: '1rem 1.5rem',
  fontWeight: 500,
  textAlign: 'left',
  borderTop: '1px solid #dee2e6',
  borderBottom: '1px solid #dee2e6',
  fontSize: '0.75rem',

  '&:first-of-type': {
    borderTopLeftRadius: '8px',
    borderLeft: '1px solid #dee2e6',
  },

  '&:last-of-type': {
    borderTopRightRadius: '8px',
    borderRight: '1px solid #dee2e6',
  },
}));

const TD = styled(TableCell)(() => ({
  verticalAlign: 'middle',
  padding: '1rem 1.5rem',
  fontSize: '0.75rem',
}));
